import React, { useCallback, useContext, useEffect, useState } from "react";
import useMediaPlaybackState, {
  IdleState,
  PausedState,
  PlayingState,
  describeState,
} from "src/ui/hooks/useMediaPlaybackState";
import useDocumentVisibilityState from "ui/hooks/useDocumentVisibilityState";
import useMediaElementReadyState from "ui/hooks/useMediaElementReadyState";
import { useLivePlaybackUserInteractionState } from "ui/scenes/stream/LivePlaybackUserInteractionContext";
import MuteContext from "ui/scenes/stream/MuteContext";
import logger from "utils/logger";

const UNMUTE_SYNC_DELAY_MILLISECONDS = 10;

const useLiveMediaPlay = ({
  mediaRef,
  muted,
  paused,
}: {
  mediaRef: React.RefObject<HTMLMediaElement | null>;
  muted: boolean;
  paused: boolean;
}) => {
  const {
    isUserInitiatedPlayRequestRequired,
    setUserInitiatedPlayRequestRequired,
  } = useLivePlaybackUserInteractionState();

  const [isMutedLocal, setIsMutedLocal] = useState(true);
  const { setMuted } = useContext(MuteContext);
  const { visibilityState } = useDocumentVisibilityState();
  const { playbackState } = useMediaPlaybackState(mediaRef);
  const { readyState } = useMediaElementReadyState(mediaRef);
  const isReadyToPlay = readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;

  const startPlayback = useCallback(() => {
    const media = mediaRef.current;
    if (!media) {
      return;
    }

    if (paused) {
      logger.log(
        `[LiveMediaPlay]::[startPlayback]: skip start playback due to paused state`
      );

      return;
    }

    if (!isReadyToPlay) {
      logger.log(
        `[LiveMediaPlay]::[startPlayback]: skip start playback due to ready state`
      );

      return;
    }

    logger.log(
      `[LiveMediaPlay]::[startPlayback]: isMutedLocal=${isMutedLocal} - attempting to start playback`
    );

    // NOTE: Do not use async function here
    const playResult: Promise<void> | undefined = media.play();
    if (typeof playResult === "undefined") {
      return;
    }
    playResult
      .then(() => {
        logger.log(
          `[LiveMediaPlay]::[startPlayback]: isMutedLocal=${isMutedLocal}, isUserInitiatedPlayRequestRequired=${isUserInitiatedPlayRequestRequired} - video playback started`
        );
      })
      .catch((error) => {
        logger.log(
          `[LiveMediaPlay]::[startPlayback]: isMutedLocal=${isMutedLocal}, isUserInitiatedPlayRequestRequired=${isUserInitiatedPlayRequestRequired} - video play start error`,
          error
        );
        if (error instanceof DOMException) {
          if (error.name === "NotAllowedError") {
            setMuted(true);
            if (isMutedLocal) {
              logger.log(
                `[LiveMediaPlay]::[startPlayback]: isMutedLocal=${isMutedLocal}, isUserInitiatedPlayRequestRequired=${isUserInitiatedPlayRequestRequired} - need user interaction to play`
              );
              setUserInitiatedPlayRequestRequired(true);
            } else {
              logger.log(
                `[LiveMediaPlay]::[startPlayback]: isMutedLocal=${isMutedLocal}, isUserInitiatedPlayRequestRequired=${isUserInitiatedPlayRequestRequired} - will try with muted video`
              );
            }
          }
        }
      });
  }, [
    mediaRef,
    isMutedLocal,
    setMuted,
    paused,
    isReadyToPlay,
    isUserInitiatedPlayRequestRequired,
    setUserInitiatedPlayRequestRequired,
  ]);

  const stopPlayback = useCallback(() => {
    const media = mediaRef.current;
    if (!media) {
      return;
    }

    if (!paused) {
      logger.log(
        `[LiveMediaPlay]::[stopPlayback]: skip stop playback due to unpaused state`
      );

      return;
    }

    if (playbackState !== PlayingState) {
      logger.log(
        `[LiveMediaPlay]::[stopPlayback]: video playback is not active`
      );

      return;
    }

    logger.log(`[LiveMediaPlay]::[stopPlayback]: will pause video playback`);
    media.pause();
  }, [mediaRef, paused, playbackState]);

  useEffect(() => {
    if (!paused) {
      return;
    }
    stopPlayback();
  }, [playbackState, paused, stopPlayback]);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) {
      return;
    }

    if (paused) {
      logger.log(
        `[LiveMediaPlay]::[startPlayback]: skip start playback due to paused state`
      );

      return;
    }

    if (visibilityState === "hidden") {
      logger.log(
        "[LiveMediaPlay]: skip effect in case of document is not visible"
      );

      return;
    }

    if (isUserInitiatedPlayRequestRequired) {
      logger.log(
        `[LiveMediaPlay]: isMutedLocal=${isMutedLocal}, isUserInitiatedPlayRequestRequired=${isUserInitiatedPlayRequestRequired} need user interaction`
      );

      return;
    }

    if (playbackState === IdleState || playbackState === PausedState) {
      startPlayback();
    }
  }, [
    mediaRef,
    startPlayback,
    playbackState,
    isMutedLocal,
    paused,
    setMuted,
    isUserInitiatedPlayRequestRequired,
    visibilityState,
  ]);

  useEffect(() => {
    if (playbackState === PlayingState) {
      return;
    }
    if (isMutedLocal) {
      return;
    }
    /// Mobile Safari has policy to pause concurrently playing media session (<video> and <audio> elements)
    /// in case of LOUD playback started over <video> or <audio> element.
    /// See: https://github.com/WebKit/WebKit/blob/567d1df2e1d9d1496e659b5f984414644c66b175/Source/WebCore/platform/audio/ios/MediaSessionManagerIOS.mm#L85
    /// This behavior is independent of automatic playback restrictions commonly implemented in browsers.
    /// See: https://github.com/WebKit/WebKit/blob/567d1df2e1d9d1496e659b5f984414644c66b175/Source/WebCore/platform/audio/PlatformMediaSessionManager.cpp#L292-L300

    /// So the only reliable way to start automatic playback taking into account both auto-play restirction policy and concurrent playback policy
    /// is to always start muted playback and try to unmute it shortly after start when user want umuted playback. In case of automatic lound playback
    /// is not allowed browser will pause player, which is then handled to mute player back and restart play()

    logger.log(`[LiveMediaPlay]: will mute local state`);
    setIsMutedLocal(true);
  }, [isMutedLocal, setIsMutedLocal, playbackState]);

  useEffect(() => {
    if (muted) {
      logger.log(`[LiveMediaPlay]: will sync local mute state`);
      setIsMutedLocal(true);

      return;
    }

    if (paused) {
      logger.log(`[LiveMediaPlay]: will skip unmute sync in paused state`);

      return;
    }

    if (readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      logger.log(
        `[LiveMediaPlay]: will skip unmute sync effect in ${readyState}`
      );

      return;
    }

    const id = setTimeout(() => {
      logger.log("[LiveMediaPlay]: will unmute local state after delay");
      setIsMutedLocal(muted);
    }, UNMUTE_SYNC_DELAY_MILLISECONDS);

    return () => {
      clearTimeout(id);
    };
  }, [isMutedLocal, muted, paused, readyState, setIsMutedLocal]);

  useEffect(() => {
    logger.log(
      `[LiveMediaPlay]: document visibility state: ${visibilityState}`
    );
  }, [visibilityState]);

  return {
    isMuted: isMutedLocal,
    playbackState: describeState(playbackState),
  };
};

export default useLiveMediaPlay;
